import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../components/Button'
import Icon from '../components/Icon'
import MultiSelect from '../components/MultiSelect'
import { useUsersStore } from '../../stores/usersStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useAuth } from '../../contexts/AuthContext'
import { CreateUserRequest, OrganizationRole, School } from '../../types'
import { getRoles } from '../../services/roles'

const UserEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    users,
    loading,
    error,
    isCreating,
    isUpdating,
    fetchUsers,
    addUser,
    editUser,
    removeUser,
    clearError,
  } = useUsersStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  })
  const [selectedSchools, setSelectedSchools] = useState<string[]>([])
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [availableRoles, setAvailableRoles] = useState<OrganizationRole[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)

  const isNewUser = id === 'new'
  const currentUser = !isNewUser ? users.find((u) => u._id === id) : null

  useEffect(() => {
    if (users.length === 0) {
      fetchUsers()
    }
    if (schools.length === 0) {
      fetchSchools()
    }
    const fetchAvailableRoles = async () => {
      setRolesLoading(true)
      try {
        const roles = await getRoles()
        setAvailableRoles(roles)
      } catch (err) {
        console.error('Error fetching roles:', err)
      } finally {
        setRolesLoading(false)
      }
    }
    fetchAvailableRoles()
  }, [users.length, schools.length, fetchUsers, fetchSchools])

  useEffect(() => {
    if (!isNewUser && currentUser) {
      setFormData({
        email: currentUser.email,
        password: '', // Don't pre-fill password for security
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      })

      // Set schools - handle both populated and non-populated cases
      const userSchoolIds = Array.isArray(currentUser.schools)
        ? currentUser.schools.map((school: any) =>
            typeof school === 'string' ? school : school._id,
          )
        : []
      setSelectedSchools(userSchoolIds)

      // Set role - handle both populated and non-populated cases
      const roleId =
        typeof currentUser.role === 'string'
          ? currentUser.role
          : (currentUser.role as OrganizationRole)?._id || ''
      setSelectedRole(roleId)
    }
  }, [isNewUser, currentUser])

  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  const handleSaveUser = async () => {
    if (
      !formData.email.trim() ||
      !formData.firstName.trim() ||
      !formData.lastName.trim()
    ) {
      return
    }

    // For create mode, password is required
    if (isNewUser && !formData.password.trim()) {
      return
    }

    try {
      const userData: CreateUserRequest = {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        organization:
          typeof user?.organization === 'string'
            ? user.organization
            : user?.organization._id || '',
        schools: selectedSchools,
        role: selectedRole,
      }

      if (isNewUser) {
        await addUser(userData)
      } else if (id) {
        // Edit mode - only include password if it was provided
        const updateData: any = { ...userData }
        if (!formData.password.trim()) {
          delete updateData.password
        }
        await editUser(id, updateData)
      }

      navigate('/manage/users')
    } catch (err) {
      console.error(
        isNewUser ? 'Error creating user:' : 'Error updating user:',
        err,
      )
    }
  }

  const handleDeleteUser = async () => {
    if (!currentUser) return

    if (
      window.confirm(
        `Are you sure you want to delete "${currentUser.firstName} ${currentUser.lastName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeUser(currentUser._id)
        navigate('/manage/users')
      } catch (err) {
        console.error('Error deleting user:', err)
      }
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }))
  }

  const generatePassword = () => {
    const length = 12
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }
    setFormData((prev) => ({
      ...prev,
      password,
    }))
  }

  // Filter schools by the selected organization
  const getAvailableSchools = () => {
    const targetOrganization =
      typeof user?.organization === 'string'
        ? user.organization
        : user?.organization._id

    return schools.filter(
      (school: School) => school.organization === targetOrganization,
    )
  }

  if (
    (loading && users.length === 0) ||
    (schoolsLoading && schools.length === 0) ||
    rolesLoading
  ) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isNewUser && !currentUser && !loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          User not found
        </div>
        <Button
          onClick={() => navigate('/manage/users')}
          variant="link"
          className="mt-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Users
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto space-y-6">
      {/* Header with Back Button */}
      <div>
        <Button
          onClick={() => navigate('/manage/users')}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Users
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewUser ? 'Create New User' : 'Edit User'}
        </h1>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Enter first name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Enter last name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password {!isNewUser && '(leave blank to keep current password)'}
          </label>
          <div className="relative">
            <input
              type="text"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={
                isNewUser
                  ? 'Enter password (min 6 characters)'
                  : 'Enter new password (optional)'
              }
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <Button
              type="button"
              onClick={generatePassword}
              title="Generate random password"
              variant="ghost"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              <Icon type="refresh" className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Schools multi-select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Schools
          </label>
          <MultiSelect
            options={getAvailableSchools().map((school) => ({
              _id: school._id,
              name: school.displayName,
            }))}
            selectedValues={selectedSchools}
            onChange={setSelectedSchools}
            placeholder={
              getAvailableSchools().length === 0
                ? 'No schools available in this organization'
                : 'Select schools for this user...'
            }
            className="w-full"
            disabled={getAvailableSchools().length === 0}
            fixed={true}
          />
          <p className="mt-1 text-xs text-gray-500">
            {getAvailableSchools().length === 0
              ? 'Create schools in this organization first before assigning them to users'
              : 'Select which schools in the organization this user will have access to'}
          </p>
        </div>

        {/* Role selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a role...</option>
            {availableRoles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))}
          </select>
          {selectedRole && (
            <p className="mt-1 text-xs text-gray-500">
              {availableRoles.find((r) => r._id === selectedRole)?.description}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            {!isNewUser && (
              <Button
                onClick={handleDeleteUser}
                disabled={isUpdating}
                variant="link"
                className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
              >
                Delete User
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/manage/users')}
              disabled={isCreating || isUpdating}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={
                isCreating ||
                isUpdating ||
                !formData.email.trim() ||
                (isNewUser && !formData.password.trim()) ||
                !formData.firstName.trim() ||
                !formData.lastName.trim()
              }
              variant="primary"
              loading={isCreating || isUpdating}
            >
              {isNewUser ? 'Create User' : 'Update User'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserEdit
